/**
 * Security Log Service
 *
 * Logs security-related events for auditing and monitoring purposes.
 * In development mode, logs are sent to console for debugging.
 * In production mode, logs are filtered and could be sent to a backend audit service.
 *
 * Important: Sensitive data (passwords, tokens, etc.) is automatically filtered from logs.
 *
 * @example
 * ```typescript
 * constructor(private securityLog: SecurityLogService) {}
 *
 * onLogin() {
 *   this.securityLog.logAuthEvent('login');
 * }
 *
 * onSuspiciousInput(input: string) {
 *   this.securityLog.logSuspiciousActivity('XSS attempt detected in user input');
 * }
 * ```
 */

import { Injectable } from '@angular/core';
import { SecurityEventType, SECURITY_CONFIG } from '../constants/security.constants';
import { environment } from '../../../environments/environment';

/**
 * Security log entry interface
 */
interface SecurityLogEntry {
  /**
   * Timestamp of the event
   */
  timestamp: Date;

  /**
   * Type of security event
   */
  eventType: string;

  /**
   * Event message
   */
  message: string;

  /**
   * Additional event details (sensitive data filtered)
   */
  details?: any;

  /**
   * User agent string
   */
  userAgent?: string;

  /**
   * Current URL
   */
  url?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SecurityLogService {
  /**
   * In-memory log storage (for development/debugging)
   * Limited to last 100 entries
   */
  private logs: SecurityLogEntry[] = [];

  /**
   * Maximum number of logs to keep in memory
   */
  private readonly maxLogsInMemory = 100;

  /**
   * Logs an authentication-related event
   *
   * @param event - Type of authentication event
   * @param details - Optional additional details about the event
   *
   * @example
   * ```typescript
   * // Log successful login
   * this.securityLog.logAuthEvent('login');
   *
   * // Log failed login with details
   * this.securityLog.logAuthEvent('failed', { email: 'user@example.com' });
   *
   * // Log logout
   * this.securityLog.logAuthEvent('logout');
   * ```
   */
  logAuthEvent(
    event: 'login' | 'logout' | 'refresh' | 'failed',
    details?: any
  ): void {
    let eventType: SecurityEventType;
    let message: string;

    switch (event) {
      case 'login':
        eventType = SecurityEventType.LOGIN;
        message = 'User logged in successfully';
        break;
      case 'logout':
        eventType = SecurityEventType.LOGOUT;
        message = 'User logged out';
        break;
      case 'refresh':
        eventType = SecurityEventType.TOKEN_REFRESH;
        message = 'Authentication token refreshed';
        break;
      case 'failed':
        eventType = SecurityEventType.LOGIN_FAILED;
        message = 'Login attempt failed';
        break;
    }

    this.log(eventType, message, details);
  }

  /**
   * Logs a security warning
   *
   * Used for events that may indicate security issues but are not critical.
   *
   * @param message - Warning message
   * @param details - Optional additional details
   *
   * @example
   * ```typescript
   * this.securityLog.logSecurityWarning(
   *   'Multiple failed login attempts detected',
   *   { attempts: 3, timespan: '5 minutes' }
   * );
   * ```
   */
  logSecurityWarning(message: string, details?: any): void {
    this.log('security_warning', message, details);
  }

  /**
   * Logs suspicious activity that may indicate an attack
   *
   * @param activity - Description of the suspicious activity
   * @param details - Optional additional details
   *
   * @example
   * ```typescript
   * this.securityLog.logSuspiciousActivity(
   *   'Potential XSS attack detected',
   *   { input: '<script>...</script>', field: 'comment' }
   * );
   * ```
   */
  logSuspiciousActivity(activity: string, details?: any): void {
    this.log(
      SecurityEventType.SUSPICIOUS_ACTIVITY,
      `Suspicious activity: ${activity}`,
      details
    );
  }

  /**
   * Logs when a token has expired
   *
   * @param details - Optional additional details
   */
  logTokenExpired(details?: any): void {
    this.log(
      SecurityEventType.TOKEN_EXPIRED,
      'Authentication token has expired',
      details
    );
  }

  /**
   * Logs when a session times out due to inactivity
   *
   * @param details - Optional additional details
   */
  logSessionTimeout(details?: any): void {
    this.log(
      SecurityEventType.SESSION_TIMEOUT,
      'Session timed out due to inactivity',
      details
    );
  }

  /**
   * Logs an XSS attempt
   *
   * @param details - Details about the XSS attempt
   */
  logXssAttempt(details?: any): void {
    this.log(
      SecurityEventType.XSS_ATTEMPT,
      'Potential XSS attack attempt detected',
      details
    );
  }

  /**
   * Logs a SQL injection attempt
   *
   * @param details - Details about the SQL injection attempt
   */
  logSqlInjectionAttempt(details?: any): void {
    this.log(
      SecurityEventType.SQL_INJECTION_ATTEMPT,
      'Potential SQL injection attack attempt detected',
      details
    );
  }

  /**
   * Logs unauthorized access attempts
   *
   * @param resource - The resource that was accessed without authorization
   * @param details - Optional additional details
   */
  logUnauthorizedAccess(resource: string, details?: any): void {
    this.log(
      SecurityEventType.UNAUTHORIZED_ACCESS,
      `Unauthorized access attempt to: ${resource}`,
      details
    );
  }

  /**
   * Retrieves recent security logs
   *
   * Used for debugging or displaying security events in admin interfaces.
   *
   * @param count - Number of recent logs to retrieve (default: 50)
   * @returns Array of recent security log entries
   *
   * @example
   * ```typescript
   * const recentLogs = this.securityLog.getRecentLogs(20);
   * console.log('Recent security events:', recentLogs);
   * ```
   */
  getRecentLogs(count: number = 50): SecurityLogEntry[] {
    return this.logs.slice(-count);
  }

  /**
   * Clears all stored logs
   *
   * @example
   * ```typescript
   * this.securityLog.clearLogs();
   * ```
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Core logging method
   *
   * Filters sensitive data, adds context, and sends logs to appropriate destination.
   *
   * @param eventType - Type of security event
   * @param message - Log message
   * @param details - Additional details
   */
  private log(eventType: string, message: string, details?: any): void {
    const logEntry: SecurityLogEntry = {
      timestamp: new Date(),
      eventType,
      message,
      details: this.filterSensitiveData(details),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Add to in-memory logs
    this.logs.push(logEntry);

    // Keep only the most recent logs
    if (this.logs.length > this.maxLogsInMemory) {
      this.logs.shift();
    }

    // In development, log to console
    if (!environment.production) {
      this.logToConsole(logEntry);
    } else {
      // In production, you might want to send logs to a backend service
      // this.sendToBackend(logEntry);
    }
  }

  /**
   * Filters sensitive data from log details
   *
   * Removes or redacts sensitive fields like passwords, tokens, etc.
   *
   * @param data - Data to filter
   * @returns Filtered data safe for logging
   */
  private filterSensitiveData(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const filtered = { ...data };

    // Remove sensitive fields
    SECURITY_CONFIG.SENSITIVE_FIELDS.forEach(field => {
      if (field in filtered) {
        filtered[field] = '[REDACTED]';
      }
    });

    // Recursively filter nested objects
    Object.keys(filtered).forEach(key => {
      if (filtered[key] && typeof filtered[key] === 'object') {
        filtered[key] = this.filterSensitiveData(filtered[key]);
      }
    });

    return filtered;
  }

  /**
   * Logs entry to browser console
   *
   * Used in development mode for debugging.
   *
   * @param entry - Log entry to output
   */
  private logToConsole(entry: SecurityLogEntry): void {
    const { timestamp, eventType, message, details } = entry;
    const formattedTime = timestamp.toISOString();

    console.group(`[SECURITY LOG] ${formattedTime} - ${eventType}`);
    console.log('Message:', message);
    if (details) {
      console.log('Details:', details);
    }
    console.log('URL:', entry.url);
    console.groupEnd();
  }

  /**
   * Sends log entry to backend service (placeholder)
   *
   * In a production application, implement this to send logs to your audit service.
   *
   * @param entry - Log entry to send
   */
  private sendToBackend(entry: SecurityLogEntry): void {
    // TODO: Implement backend logging
    // Example:
    // this.http.post('/api/audit/security-logs', entry).subscribe();
  }
}
